import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
  inject,
  signal,
  computed,
  Input,
  Output,
  EventEmitter,
  effect,
} from "@angular/core";
import { Router } from "@angular/router";
import { Subject } from "rxjs";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { TAGS, type Tag } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { TableRow } from "../tables/transaction-table/transaction-table";
import { GlossRouteEnum, GlossRoutes } from "../../models/enum/routes.enum";
import {
  ConfirmationDialogService,
  CtaNotificationData,
} from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { DateUtil } from "../../shared/utils/helper.date/date-util";
import { getTransactionAmount } from "../../shared/utils/helper.transactionView/amount";
import { ClickOutsideReason } from "@bitwarden/angular/directives/click-outside.directive";
import { CategoryView } from "@bitwarden/web-vault/app/models/view/category/category.view";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { CalculationPastStoreService } from "@bitwarden/web-vault/app/services/store/calculation-past/calculation-past.store.service";
import { getName } from "@bitwarden/web-vault/app/shared/utils/helper-string";
import {
  isHiddenSystemBalanceTransaction,
  resolveInstitutionName,
  resolveTransactionDescription,
} from "../tables/transaction-table/past-transaction-table.subscription";
import { PLUGIN_CATALOG, isPluginAvailableOn } from "../../models/data/plugin-catalog";
import { PluginCardData } from "../plugin-card/plugin-card.component";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";
import { PlatformUtilsService } from "@vault/common/abstractions/platformUtils.service";
import { UpgradeNavigationService } from "@bitwarden/web-vault/app/services/subscription/upgrade-navigation.service";
import {
  CHATBOT_PLUGIN_NAME,
  PluginConfigService,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ChatbotService } from "@bitwarden/web-vault/app/services/chatbot/chatbot.service";
import { SM_BREAKPOINT, observeBreakpoint } from "@bitwarden/web-vault/app/shared/breakpoints";
import { closeAfterExitAnimation } from "@bitwarden/web-vault/app/shared/utils/exit-animation.util";

interface Data {
  id?: string;
  name?: string;
  code?: string;
  description?: string;
  postDate?: Date;
  title?: string;
}

type ParsedData = {
  id: string;
  value: string;
  type: string;
  date?: Date;
};

const ACCOUNT = "account";
const CABINET = "cabinet";
const CATEGORY = "category";
const SYMBOL = "symbol";
const TRANSACTION = "transaction";
const PLUGIN = "plugin";
const HISTORY_KEY = "history";
const MAX_HISTORY = 5;

@Component({
  selector: "app-search-bar",
  templateUrl: "./search-bar.component.html",
})
export class SearchBarComponent implements OnInit, OnDestroy {
  constructor(private cdr: ChangeDetectorRef) {}

  private readonly elRef = inject(ElementRef);
  private readonly router = inject(Router);
  private readonly userStoreService = inject(UserStoreService);
  private readonly transactionStore = inject(TransactionStoreService);
  private readonly calculationPastStore = inject(CalculationPastStoreService);
  private confirmationDialogService = inject(ConfirmationDialogService);
  private readonly upgradeNavigation = inject(UpgradeNavigationService);
  private readonly roleAccessService = inject(RoleAccessService);
  private readonly platformUtils = inject(PlatformUtilsService);
  private readonly pluginConfigService = inject(PluginConfigService);
  private readonly chatbotService = inject(ChatbotService);
  private readonly i18nService = inject(I18nService);
  private readonly destroy$ = new Subject<void>();

  @Input() transactions: TableRow[];
  @Output() isSearchFocused = new EventEmitter<boolean>();
  @Output() transactionSelected = new EventEmitter<TableRow>();
  @ViewChild("searchInput") searchInput!: ElementRef<HTMLInputElement>;
  @ViewChild("searchIcon") searchIcon?: ElementRef<HTMLButtonElement>;
  @ViewChild("tagsRow") tagsRow?: ElementRef<HTMLDivElement>;
  @ViewChild("searchPanel") private searchPanel?: ElementRef<HTMLDivElement>;
  tags: Tag[] = Object.values(TAGS);
  input = signal("");
  selectedTag = signal<Tag>(TAGS.ALL);
  isVisible = signal(false);
  isFocused = signal(false);
  /** Below `sm` the focused search bar is a full-screen page, which slides in from the right. */
  protected readonly isSmallScreen = observeBreakpoint(SM_BREAKPOINT);
  canScrollTagsLeft = signal(false);
  canScrollTagsRight = signal(false);
  private historyCache = signal<string[]>([]);
  private tagsObserver?: MutationObserver;

  result = computed(() => this.getResult());
  existInDB = computed(() => !!this.input() && this.result().length > 0);
  hasHeader = computed(() => this.selectedTag() === TAGS.ALL);

  history = computed(() => {
    const tag = this.selectedTag();
    const historyCache = this.historyCache();

    if (tag === TAGS.ALL) {
      return historyCache;
    }
    return historyCache.filter((data) => {
      return this.parseData(data).type === tag;
    });
  });

  sortData = computed(() => {
    const tag = this.selectedTag();
    const result = this.result();
    const types = Object.values(TAGS).slice(1);
    const typeMap = new Map<Tag, string[]>();
    const group: { type: Tag; items: string[]; total: number; ambiguousDateKeys: Set<string> }[] =
      [];
    const getTime = (itemStr: string) => {
      const d = this.parseData(itemStr).date;
      return DateUtil.getTime(d);
    };

    if (tag !== TAGS.ALL) {
      const sorted = [...result].sort((a, b) => getTime(b) - getTime(a));
      return [
        {
          type: tag,
          items: sorted,
          total: sorted.length,
          ambiguousDateKeys: this.getAmbiguousDateKeys(sorted),
        },
      ];
    }

    result.forEach((item) => {
      const type = this.parseData(item).type as Tag;

      if (!typeMap.has(type)) {
        typeMap.set(type, []);
        group.push({ type, items: typeMap.get(type)!, total: 0, ambiguousDateKeys: new Set() });
      }
      typeMap.get(type)!.push(item);
    });

    group.forEach((g) => {
      g.total = g.items.length;
      g.items.sort((a, b) => getTime(b) - getTime(a));
      g.items = g.items.slice(0, 3);
      g.ambiguousDateKeys = this.getAmbiguousDateKeys(g.items);
    });

    return group.sort((a, b) => types.indexOf(a.type) - types.indexOf(b.type));
  });

  isFreeUser = computed(() => {
    const activeSub = this.userStoreService.subscription.activeSubscription();
    return activeSub?.tier === "free";
  });

  private readonly transactionSearchResult = computed(() =>
    this.transactionStore.search(this.input()),
  );

  readonly emitSearchFocusedEffect = effect(() => {
    this.isSearchFocused.emit(this.isFocused());
  });

  readonly tagsScrollEffect = effect(() => {
    if (this.isFocused()) {
      setTimeout(() => {
        this.updateTagsScrollState();
        this.observeTagsRow();
      });
    } else {
      this.tagsObserver?.disconnect();
      this.tagsObserver = undefined;
    }
  });

  ngOnInit(): void {
    this.fetchHistory();
  }

  ngOnDestroy(): void {
    this.tagsObserver?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  showInput(): void {
    this.isVisible.set(true);
  }

  /** The full-screen search on phones slides out first (see .mobile-slide-in-page). */
  hideInput(): void {
    closeAfterExitAnimation(this.searchPanel?.nativeElement, () => this.resetSearch());
  }

  private resetSearch(): void {
    this.input.set("");
    this.isVisible.set(false);
    this.isFocused.set(false);
    this.searchInput.nativeElement.blur();
  }

  clearInput(): void {
    this.input.set("");
    this.searchInput.nativeElement.focus();
  }

  focusInputField(): void {
    this.showInput();
    this.onFocus();
  }

  onTagClick(tag: Tag): void {
    if (this.selectedTag() === tag) {
      return;
    }
    this.selectedTag.set(tag);
  }

  onViewAllClick(tag: Tag): void {
    this.onTagClick(tag);
  }

  onFocus(): void {
    this.isFocused.set(true);
    this.cdr.detectChanges();
    this.searchInput.nativeElement.focus();
  }

  /**
   * ClickOutsideDirective's Escape listener is global to the document — an Escape press meant
   * to dismiss something else on the page (e.g. an unrelated confirmation dialog) must not
   * silently wipe an in-progress typed search query. isFocused() alone doesn't reset on blur, so
   * clicking non-focusable chrome inside the open panel (tags row padding, a result-list header,
   * dragging the results scrollbar) leaves activeElement outside this host while the panel is
   * still logically open — a pure focus-containment guard would make Escape a no-op there until
   * the panel is dismissed another way. So, mirroring category-select's hybrid guard: only
   * require focus containment when there's typed input to lose; an empty query has nothing at
   * risk, so Escape closes it unconditionally. A real outside click can't have either problem
   * (ClickOutsideDirective's mousedown/mouseup targeting is already specific to this host), so
   * only Escape needs the extra check. Tab out of the search bar ("focus-out", via
   * appClickOutsideFocusOut) closes it like an outside click.
   */
  onClickOutside(reason: ClickOutsideReason = "outside-click"): void {
    if (!this.isFocused()) {
      return;
    }
    if (
      reason === "escape" &&
      this.input() &&
      !this.elRef.nativeElement.contains(document.activeElement)
    ) {
      return;
    }
    // hideInput() blurs the field, which drops keyboard focus to <body>. After an Escape from
    // inside the bar, hand focus to the search button instead (refocusing the field itself would
    // reopen the panel via (focus)).
    const restoreFocus =
      reason === "escape" && this.elRef.nativeElement.contains(document.activeElement);
    this.hideInput();
    if (restoreFocus) {
      this.searchIcon?.nativeElement.focus();
    }
  }

  onTagsScroll(): void {
    this.updateTagsScrollState();
  }

  @HostListener("window:resize")
  onWindowResize(): void {
    this.updateTagsScrollState();
  }

  scrollTags(direction: number): void {
    this.tagsRow?.nativeElement.scrollBy({ left: direction * 120, behavior: "smooth" });
  }

  private updateTagsScrollState(): void {
    const el = this.tagsRow?.nativeElement;
    if (!el) {
      this.canScrollTagsLeft.set(false);
      this.canScrollTagsRight.set(false);
      return;
    }
    this.canScrollTagsLeft.set(el.scrollLeft > 0);
    this.canScrollTagsRight.set(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }

  // Content (e.g. the async-gated Ask AI pill) can be inserted after this row first
  // renders, changing scrollWidth without a scroll/resize event — watch for that too.
  private observeTagsRow(): void {
    const el = this.tagsRow?.nativeElement;
    if (!el || this.tagsObserver) {
      return;
    }
    this.tagsObserver = new MutationObserver(() => this.updateTagsScrollState());
    this.tagsObserver.observe(el, { childList: true, subtree: true });
  }

  highlightMatch(data: string): string {
    const input = this.input().toLowerCase();
    const escapedInput = input.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`(${escapedInput})`, "gi"); // g: global, i: case insensitive

    return data.replace(
      regex,
      `<mark class="tw-text-dt-custom-teal-9 tw-bg-transparent">$1</mark>`,
    ); // $1: the first capturing group
  }

  trackById(data: string): string {
    return this.parseData(data).id;
  }

  // The chatbot panel (<app-chatbot> in the user layout) only renders while the Chatbot plugin is
  // enabled, so without it Ask AI points the user at the plugin instead of opening a dead panel.
  onAskAiClick(): void {
    this.hideInput();
    if (this.pluginConfigService.isChatbotPluginEnabled()) {
      this.chatbotService.open();
      return;
    }
    this.platformUtils.showToast(
      "info",
      this.i18nService.t("chatbotNotInstalledTitle"),
      this.i18nService.t("chatbotNotInstalledMessage"),
      {
        payload: {
          actionLabel: this.i18nService.t("viewPlugin"),
          onAction: () =>
            void this.router.navigate(["/", GlossRoutes.Plugins], {
              queryParams: { pluginId: CHATBOT_PLUGIN_NAME },
            }),
        },
      },
    );
  }

  onPremiumClick() {
    const i18nService = this.confirmationDialogService["i18nService"];
    const data: CtaNotificationData = {
      title: i18nService.t("lookingForTransaction"),
      message: i18nService.t("callToPremiumMessageSearch"),
      cancel: { method: () => {}, title: i18nService.t("maybeLater") },
      confirm: {
        method: () => this.upgradeNavigation.goToUpgrade(),
        title: i18nService.t("startFreeTrial"),
      },
      reminder: i18nService.t("cancelPremiumReminder"),
    };
    this.confirmationDialogService.ctaNotification(data);
  }

  private parseData(data: string): ParsedData {
    return JSON.parse(data) as ParsedData;
  }

  private parseResult(data: Data[], tag: string): string[] {
    return data
      .map((item) => {
        let value: string;
        let id: string;
        const date = item.postDate;

        switch (tag) {
          case ACCOUNT:
          case CABINET:
          case CATEGORY:
            value = item.name;
            id = item.id;
            break;
          case SYMBOL:
            value = item.code;
            id = item.code;
            break;
          case TRANSACTION:
            value = item.description;
            id = item.id;
            break;
          case PLUGIN:
            value = item.title;
            id = item.id;
            break;
        }
        if (id && value) {
          const result = { value, id, type: tag, date };
          return JSON.stringify(result);
        }
      })
      .filter((value) => !!value);
  }

  private getUserResult() {
    return this.userStoreService.search(this.input());
  }

  private getTransactionResult() {
    return this.transactionSearchResult();
  }

  private getCategoryResult(): CategoryView[] {
    const term = this.input().trim().toLowerCase();
    if (!term || !this.userStoreService.categories.categoryViews()) {
      return [];
    }
    return this.userStoreService.categories
      .categoriesWithoutDefault()
      .filter((category) => category.name?.toLowerCase().includes(term));
  }

  // Matches only the plugin's name — like every other tag here (account/cabinet/category
  // match `.name`, symbol matches `.code`). Also matching the long marketing `description`
  // (as the Plugins page's own search does) makes short terms match almost every plugin,
  // since generic words like "your"/"account"/"open" show up in most of them.
  //
  // Beta-only plugins are filtered out for non-beta users the same way the Plugins page's
  // own displayedPlugins does — otherwise the global search bar would surface (and let you
  // open the install dialog for) plugins that page deliberately hides.
  private getPluginResult(): PluginCardData[] {
    const term = this.input().trim().toLowerCase();
    if (!term) {
      return [];
    }
    return PLUGIN_CATALOG.filter(
      (plugin) =>
        // The Gloss Vault browser extension can't be installed in the native iOS/Android apps — hide it
        // from global search there too, matching the Plugins page's own exclusion (#5613).
        isPluginAvailableOn(plugin.id, this.platformUtils.getDevice()) &&
        (!plugin.isBetaAccess || this.roleAccessService.isBetaUserSignal()) &&
        plugin.title.toLowerCase().includes(term),
    );
  }

  private getResult(): string[] {
    const tag = this.selectedTag();
    const inputValue = this.input().trim();
    const userData = this.getUserResult();
    const transactionData = this.getTransactionResult();
    const categoryData = this.getCategoryResult();
    const pluginData = this.getPluginResult();
    const transactionOnly = transactionData.transaction.filter(
      (transaction) => transaction.definition !== TransactionStatusEnum.empty_account_balance,
    );

    const dataMap: Record<Tag, () => string[]> = {
      [TAGS.ALL]: () => [
        ...this.parseResult(userData.account, ACCOUNT),
        ...this.parseResult(userData.cabinet, CABINET),
        ...this.parseResult(categoryData, CATEGORY),
        ...this.parseResult(transactionData.symbol, SYMBOL),
        ...this.parseResult(transactionOnly, TRANSACTION),
        ...this.parseResult(pluginData, PLUGIN),
      ],
      [TAGS.ACCOUNT]: () => this.parseResult(userData.account, ACCOUNT),
      [TAGS.CABINET]: () => this.parseResult(userData.cabinet, CABINET),
      [TAGS.CATEGORY]: () => this.parseResult(categoryData, CATEGORY),
      [TAGS.SYMBOL]: () => this.parseResult(transactionData.symbol, SYMBOL),
      [TAGS.TRANSACTION]: () => this.parseResult(transactionOnly, TRANSACTION),
      [TAGS.PLUGIN]: () => this.parseResult(pluginData, PLUGIN),
    };

    return inputValue ? dataMap[tag]() : this.history();
  }

  private fetchHistory(): void {
    try {
      const history = JSON.parse(localStorage.getItem(HISTORY_KEY) || "[]");
      this.historyCache.set(Array.isArray(history) ? history : []);
    } catch {
      this.historyCache.set([]);
    }
  }

  private updateHistory(history: string[]): void {
    const newHistory = history.slice(0, MAX_HISTORY);
    this.historyCache.set(newHistory);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(newHistory));
  }

  handleHistory(data: string): void {
    if (data) {
      const historyCache = this.historyCache();
      const updated = [data, ...historyCache.filter((item) => item !== data)];
      this.updateHistory(updated);
    }
  }

  clearHistory(): void {
    const tag = this.selectedTag();
    if (tag === TAGS.ALL) {
      this.updateHistory([]);
    } else {
      const historyCache = this.historyCache();
      const updatedHistory = historyCache.filter((data) => {
        return this.parseData(data).type !== tag;
      });
      this.updateHistory(updatedHistory);
    }
  }

  /**
   * Builds a TableRow-shaped object straight from a raw TransactionView, for the case where the
   * transaction isn't present in `this.transactions` (the currently filtered rows) — see the
   * `transaction` handler in `redirect()`. Mirrors the mapping in
   * PastTransactionTableSubscription.source, minus fields (balance, granularity, status) the
   * detail panel (TransactionDetailsComponent) never reads.
   */
  private buildRowFromTransaction(transaction: TransactionView): TableRow {
    const accountViews = this.userStoreService.accounts.accountViews() ?? [];
    const institutionViews = this.userStoreService.institutions.institutionViews() ?? [];
    // Found once and reused below for both `account` and `accountView`, instead of scanning
    // accountViews for the same id twice.
    const account = accountViews.find((acc) => acc.id === transaction.accountLink.id);

    return {
      // Cast matches getName()'s own declared (if optimistic) return type of `string` for a
      // missing match — same "TableRow requires it, the account may not exist" case as
      // `accountView` below.
      account: account?.name as string,
      institution: resolveInstitutionName(
        getName(institutionViews, transaction.c_institutionLink.id),
      ),
      origin: transaction.origin,
      // c_valuation is only set once normalization resolves (setValuationOnTransaction) - a
      // search hit whose valuation never resolved must still open rather than throw here.
      symbol: transaction.c_valuation?.normalizedValue?.symbol ?? "",
      amount: transaction.c_valuation?.normalizedValue?.amount ?? 0,
      transactionView: transaction,
      transactionDate: transaction.postDate.toISOString(),
      description: resolveTransactionDescription(transaction),
      balance: 0,
      // TableRow types this as required (matching TableSource), but the search result can
      // outlive its account (e.g. deleted account) — same nullable lookup as the mapping this
      // mirrors (PastTransactionTableSubscription.source), just made explicit here.
      accountView: account as AccountView,
    };
  }

  async redirect(data: string): Promise<void> {
    const { id, value, type } = this.parseData(data);
    const navigate = (path: string, params: object, options: object = {}): Promise<boolean> =>
      this.router.navigate([path], { queryParams: params, ...options });

    const navigationMap: Record<string, () => Promise<boolean>> = {
      account: async () => {
        const account = this.getUserResult().account.find((acc) => acc.id === id);
        if (account) {
          return !!(await navigate(`${GlossRouteEnum.account}`, { accountId: account.id }));
        }
        return false;
      },
      cabinet: async () => {
        const cabinet = this.getUserResult().cabinet.find((cab) => cab.id === id);
        if (cabinet) {
          return !!(await navigate(`${GlossRouteEnum.cabinet}`, { fileId: cabinet.id }));
        }
        return false;
      },
      category: async () => {
        // Resolve against the full category list, not getCategoryResult() — that method
        // is gated on a non-empty search term and returns [] when redirecting from history.
        const category = (this.userStoreService.categories.categoryViews() ?? []).find(
          (cat) => cat.id === id,
        );
        if (category) {
          return !!(await navigate(`${GlossRouteEnum.categories}`, { categoryId: category.id }));
        }
        return false;
      },
      symbol: async () => {
        const symbol = this.getTransactionResult().symbol.find((sym) => sym.code === value);
        if (symbol) {
          return !!(await navigate(`${GlossRouteEnum.symbol}`, { symbolCode: symbol.code }));
        }
        return false;
      },
      // Transactions don't navigate via the router — the parent component handles
      // display (e.g. opening a side panel) by listening to transactionSelected.
      transaction: async () => {
        const transaction = this.getTransactionResult().transaction.find((txn) => txn.id === id);
        // A system-generated, zero-amount balance row (closing/opening/manual_balance/
        // empty_account_balance) is one PastTransactionTableSubscription.source itself always
        // filters out — the grid never shows it, so it must not be forced open here either.
        if (!transaction || isHiddenSystemBalanceTransaction(transaction)) {
          return false;
        }

        const matchingTxn = this.transactions.find(
          (txn) => txn.transactionView?.id === transaction.id,
        );
        if (matchingTxn) {
          this.transactionSelected.emit(matchingTxn);
          return true;
        }

        // Not in the currently displayed (filtered) rows — the transaction exists but is
        // hidden by active page filters or the date range (#5058). Widen both just enough to
        // include it, then open directly from the raw transaction rather than waiting for the
        // background recalculation to regenerate `this.transactions`.
        this.calculationPastStore.filters.ensureTransactionVisible(transaction);
        this.calculationPastStore.period.ensureDateVisible(transaction.postDate);
        this.transactionSelected.emit(this.buildRowFromTransaction(transaction));
        return true;
      },
      // Navigates to the Plugins page first, which opens the same detail dialog
      // (via its own pluginId query param handling) as clicking its card there.
      plugin: async () => {
        // Re-check device availability and beta access here too (not just in getPluginResult): a
        // recent-search row saved before #5821 (or before the user's beta access changed) could point
        // at a plugin that is no longer offered on this device. Without the device check, tapping a
        // saved Gloss Vault extension row on native navigated to a page with no card (dead-end) and
        // re-saved the row so it never aged out (#5948).
        const plugin = PLUGIN_CATALOG.find(
          (p) =>
            p.id === id &&
            isPluginAvailableOn(p.id, this.platformUtils.getDevice()) &&
            (!p.isBetaAccess || this.roleAccessService.isBetaUser()),
        );
        if (plugin) {
          return !!(await navigate(`/${GlossRoutes.Plugins}`, { pluginId: plugin.id }));
        }
        return false;
      },
    };

    const handler = navigationMap[type];
    if (!handler) {
      return;
    }

    const navigated = await handler();
    if (navigated) {
      this.handleHistory(data);
      this.hideInput();
    }
  }

  private readonly headerI18nKeys: Partial<Record<Tag, string>> = {
    [TAGS.ACCOUNT]: "accounts",
    [TAGS.TRANSACTION]: "transactions",
    [TAGS.CATEGORY]: "categories",
    [TAGS.SYMBOL]: "symbols",
    [TAGS.CABINET]: "cabinets",
    [TAGS.PLUGIN]: "plugins",
  };

  getHeader(tag: Tag): string {
    return this.headerI18nKeys[tag] ?? tag;
  }

  getData(data: string): string {
    return this.parseData(data).value;
  }

  getTransactionDate(data: string): Date {
    const d = this.parseData(data).date;
    return d ? new Date(d) : null;
  }

  private getAmbiguousDateKey(raw: string): string | null {
    const { type, value, date } = this.parseData(raw);
    if (type !== TRANSACTION || !date) {
      return null;
    }
    return `${value}|${new Date(date).toDateString()}`;
  }

  /** Keys (name + calendar day) shared by more than one item in `items` — those rows need a time label to stay distinguishable. */
  private getAmbiguousDateKeys(items: string[]): Set<string> {
    const counts = new Map<string, number>();
    for (const raw of items) {
      const key = this.getAmbiguousDateKey(raw);
      if (key) {
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    const ambiguous = new Set<string>();
    counts.forEach((count, key) => {
      if (count > 1) {
        ambiguous.add(key);
      }
    });
    return ambiguous;
  }

  /**
   * True when another transaction in the same displayed group has the same name and calendar
   * date as `item` — e.g. two separate "Anz banking payment" charges on the same day. The date
   * alone can't tell them apart, so the row also needs a time label.
   */
  hasAmbiguousDate(item: string, ambiguousDateKeys: Set<string>): boolean {
    const key = this.getAmbiguousDateKey(item);
    return !!key && ambiguousDateKeys.has(key);
  }

  /**
   * When the source transaction has no real wall-clock time (e.g. a manual entry or a bank
   * import that only supplies a date — see `enrichTransactionDate` in transaction.utils.ts),
   * `postDate` carries a synthetic time such as midnight. This label then degenerates to that
   * synthetic value and doesn't actually disambiguate same-day items sharing a name.
   */
  getTimeLabel(data: string): string | null {
    const date = this.getTransactionDate(data);
    return date ? date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : null;
  }

  getAmount(data: string): number | null {
    const { id, type } = this.parseData(data);
    if (type !== TRANSACTION) {
      return null;
    }

    const transaction = this.getTransactionResult().transaction.find((txn) => txn.id === id);
    if (!transaction) {
      return null;
    }

    const amount = getTransactionAmount(transaction, false);
    return Number.isFinite(amount) ? amount : null;
  }

  getCurrency(data: string): string | null {
    const { id, type } = this.parseData(data);
    if (type !== TRANSACTION) {
      return null;
    }

    const transaction = this.getTransactionResult().transaction.find((txn) => txn.id === id);
    return transaction?.quantity?.actualQuantity?.symbol ?? null;
  }

  getIcon(data: string): string | null {
    const { value, type } = this.parseData(data);

    switch (type) {
      case ACCOUNT: {
        const account = this.getUserResult().account.find((acc) => acc.name === value);
        return account?.type ? `${account.type}-account` : "bank-account";
      }
      case TRANSACTION:
        return "receipt";
      case CATEGORY:
        return "pie-chart";
      case CABINET:
        return "cabinet";
      case PLUGIN:
        return "plugins";
      default:
        return null;
    }
  }

  /** Cabinet results get a bordered square instead of the transaction/category filled circle. */
  getIconVariant(data: string): "background" | "border" | null {
    const { type } = this.parseData(data);
    if (type === TRANSACTION || type === CATEGORY || type === PLUGIN) {
      return "background";
    }
    if (type === CABINET) {
      return "border";
    }
    return null;
  }
}
